import { Box, Stack, Typography } from "@mui/material";
import { MaterialRead } from "src/store/api/generatedApi";
import { useMaterialColor } from "hooks/useMaterials";
import { useMemo } from "react";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { useTenantTranslation } from "hooks/formatters";
import { keyCodeToKeyName } from "components/scrapEyeLayout/hotkeys/keyCodeConversion";
import { getLabellingMaterialDisplayName } from "components/scrapEyeLabelling/utils/materialDisplayName";

// Key size and spacing constants
const KEY_SIZE = {
  default: 96,
  compact: 56,
} as const;

const KEY_GAP = {
  default: 6,
  compact: 4,
} as const;

const OFFSET_MULT = [0, 0.5, 0.83, 1.16] as const;

/**
 * Keyboard visual layout - rows as they appear on a physical keyboard.
 * Offset multipliers match real keyboard stagger (each row ~33% more than previous).
 */
export const KEYBOARD_VISUAL_ROWS = [
  { keys: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"] },
  { keys: ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"] },
  { keys: ["A", "S", "D", "F", "G", "H", "J", "K", "L", ";"] },
  { keys: ["Z", "X", "C", "V", "B", "N", "M", ",", ".", "/"] },
] as const;

type KeyboardKeyProps = Readonly<{
  hotkey: string;
  label: string;
  color: string;
  size: number;
  compact: boolean;
  isSelected?: boolean;
  isWaiting?: boolean;
  anySelected?: boolean;
  onClick?: () => void;
}>;

const KeyboardKey = ({
  hotkey,
  label,
  color,
  size,
  compact,
  isSelected = false,
  isWaiting = false,
  anySelected = false,
  onClick,
}: KeyboardKeyProps) => {
  const isClickable = onClick !== undefined;

  return (
    <Box
      onClick={onClick}
      data-material-key={isClickable ? true : undefined}
      sx={{
        width: size,
        height: size,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: compact ? 0.2 : 0.5,
        bgcolor: color,
        borderRadius: compact ? "4px" : "6px",
        border: isSelected ? "1px solid" : "1px solid rgba(255,255,255,0.15)",
        borderColor: isSelected ? "primary.main" : "rgba(255,255,255,0.15)",
        borderBottom: compact
          ? "3px solid rgba(0,0,0,0.4)"
          : "5px solid rgba(0,0,0,0.4)",
        boxShadow: compact
          ? "0 1px 0 rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)"
          : "0 2px 0 rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
        flexShrink: 0,
        overflow: "hidden",
        cursor: isClickable ? "pointer" : "default",
        transition: isClickable ? "all 0.15s ease" : undefined,
        ...(isClickable && {
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow:
              "0 4px 0 rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
          },
        }),
        ...(isWaiting && {
          animation: "pulse 1.5s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.6 },
          },
        }),
        ...(anySelected && {
          opacity: isSelected ? 1 : 0.7,
        }),
      }}
    >
      <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end" }}>
        <Typography
          sx={{
            color: "white",
            fontWeight: "bold",
            fontFamily: "monospace",
            fontSize: compact ? "0.8rem" : "1.25rem",
            lineHeight: 1,
            textShadow: compact
              ? "0 1px 1px rgba(0,0,0,0.6)"
              : "0 1px 2px rgba(0,0,0,0.6)",
          }}
        >
          {isWaiting ? "..." : hotkey}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, display: "flex", alignItems: "flex-start" }}>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.9)",
            fontSize: compact ? "0.6rem" : "0.8rem",
            lineHeight: compact ? 1.1 : 1.2,
            textShadow: "0 1px 1px rgba(0,0,0,0.5)",
            maxWidth: size - (compact ? 4 : 8),
            textAlign: "center",
            overflow: "hidden",
            whiteSpace: compact ? "nowrap" : "wrap",
            // only valid in webkit
            lineClamp: compact ? 1 : 3,
            textOverflow: "ellipsis",
            px: compact ? 0.25 : 0,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

export type MaterialHotkeyOverlayProps = Readonly<{
  materials: readonly MaterialRead[];
  mode?: "default" | "compact";
  onMaterialClick?: (materialIndex: number) => void;
  selectedMaterialIndex?: number | null;
  isWaitingForInput?: boolean;
  onUnknownClick?: () => void;
}>;

export const MaterialHotkeyOverlay = ({
  materials,
  mode = "default",
  onMaterialClick,
  selectedMaterialIndex,
  isWaitingForInput = false,
  onUnknownClick,
}: MaterialHotkeyOverlayProps) => {
  const { t } = useTenantTranslation();
  const { getColor } = useMaterialColor();
  const { getMaterialHotkey } = useHotkeyContext();

  const compact = mode === "compact";
  const keySize = KEY_SIZE[mode];
  const keyGap = KEY_GAP[mode];
  const keyUnit = keySize + keyGap;

  // Index -1 is used for the unknown material
  const hotkeyToMaterialIndex = useMemo(() => {
    const result: Record<string, number> = {};

    // Add unknown material (index -1)
    const unknownCode = getMaterialHotkey(-1);
    const unknownDisplayKey = keyCodeToKeyName(unknownCode);
    if (unknownDisplayKey) {
      result[unknownDisplayKey.toUpperCase()] = -1;
    }

    // Add regular materials
    for (let i = 0; i < materials.length; i++) {
      const code = getMaterialHotkey(i);
      const displayKey = keyCodeToKeyName(code);
      if (displayKey) {
        result[displayKey.toUpperCase()] = i;
      }
    }
    return result;
  }, [materials, getMaterialHotkey]);

  // Check if a key is visible (has any material assigned)
  const isKeyVisible = (key: string) =>
    hotkeyToMaterialIndex[key] !== undefined;

  return (
    <Box
      sx={{
        bgcolor: "rgba(20, 20, 20, 0.95)",
        borderRadius: compact ? 1.5 : 2,
        p: 1.5,
        boxShadow: compact
          ? "0 4px 16px rgba(0,0,0,0.5)"
          : "0 8px 32px rgba(0,0,0,0.5)",
        border: "1px solid rgba(255,255,255,0.1)",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <Stack spacing={`${keyGap}px`}>
        {KEYBOARD_VISUAL_ROWS.filter((row) => row.keys.some(isKeyVisible)).map(
          (row, rowIndex) => {
            const rowOffset = Math.round(
              keyUnit * (OFFSET_MULT[rowIndex % OFFSET_MULT.length] ?? 0)
            );

            // Find the last visible key index to avoid trailing invisible placeholders
            const lastVisibleIndex = row.keys.reduce(
              (last, key, idx) => (isKeyVisible(key) ? idx : last),
              -1
            );

            return (
              <Stack
                key={row.keys[0]}
                direction="row"
                spacing={`${keyGap}px`}
                sx={{ pl: `${rowOffset}px`, position: "relative" }}
              >
                {row.keys.slice(0, lastVisibleIndex + 1).map((visualKey) => {
                  const materialIndex = hotkeyToMaterialIndex[visualKey];

                  // Render invisible placeholder for unused keys to preserve layout
                  if (materialIndex === undefined) {
                    return (
                      <Box
                        key={visualKey}
                        sx={{
                          width: keySize,
                          height: keySize,
                          flexShrink: 0,
                          visibility: "hidden",
                        }}
                      />
                    );
                  }
                  const material = materials[materialIndex];

                  if (materialIndex === -1 || material === undefined) {
                    // Unknown material
                    const displayHotkey = keyCodeToKeyName(
                      getMaterialHotkey(-1)
                    );

                    const isSelected = selectedMaterialIndex === -1;
                    const isWaiting = isSelected && isWaitingForInput;
                    const label = getLabellingMaterialDisplayName(null, t);

                    return (
                      <KeyboardKey
                        key={visualKey}
                        hotkey={displayHotkey}
                        label={label}
                        color="#666"
                        size={keySize}
                        compact={compact}
                        isSelected={isSelected}
                        isWaiting={isWaiting}
                        onClick={onUnknownClick}
                        anySelected={
                          selectedMaterialIndex !== null &&
                          selectedMaterialIndex !== undefined
                        }
                      />
                    );
                  }

                  const color = getColor(material.name);

                  const displayHotkey = keyCodeToKeyName(
                    getMaterialHotkey(materialIndex)
                  );
                  const label = getLabellingMaterialDisplayName(material, t);

                  const isSelected = selectedMaterialIndex === materialIndex;
                  const isWaiting = isSelected && isWaitingForInput;

                  return (
                    <KeyboardKey
                      key={visualKey}
                      hotkey={displayHotkey}
                      label={label}
                      color={color}
                      size={keySize}
                      compact={compact}
                      isSelected={isSelected}
                      isWaiting={isWaiting}
                      anySelected={
                        selectedMaterialIndex !== null &&
                        selectedMaterialIndex !== undefined
                      }
                      onClick={
                        onMaterialClick
                          ? () => {
                              onMaterialClick(materialIndex);
                            }
                          : undefined
                      }
                    />
                  );
                })}
              </Stack>
            );
          }
        )}
      </Stack>
    </Box>
  );
};
